import { useState, useCallback } from 'react';
import { DateInput, formatDateCompact } from '@safetyhub/ui';

export interface DateRange {
  dateFrom: string; // ISO 8601 UTC
  dateTo: string;   // ISO 8601 UTC
}

type Preset = 'today' | 'week' | 'month';

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function localStartOfDay(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toISOString();
}

function localEndOfDay(dateStr: string): string {
  const d = new Date(`${dateStr}T23:59:59`);
  return d.toISOString();
}

function getPresetRange(preset: Preset): DateRange {
  const now = new Date();
  const todayStr = toLocalDateString(now);

  if (preset === 'today') {
    return { dateFrom: localStartOfDay(todayStr), dateTo: localEndOfDay(todayStr) };
  }

  if (preset === 'week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 6);
    return { dateFrom: localStartOfDay(toLocalDateString(weekAgo)), dateTo: localEndOfDay(todayStr) };
  }

  // month
  const monthAgo = new Date(now);
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  return { dateFrom: localStartOfDay(toLocalDateString(monthAgo)), dateTo: localEndOfDay(todayStr) };
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [activePreset, setActivePreset] = useState<Preset | 'custom'>('week');

  const handlePreset = useCallback((preset: Preset) => {
    setActivePreset(preset);
    onChange(getPresetRange(preset));
  }, [onChange]);

  // 빈 값(값 지우기)은 **무시한다** — 이관 전과 같다. 기간 해제 수단이 preset 세 개이므로
  // 「시작일만 없는 범위」를 만들 길을 두지 않는다(공용 `DateInput`은 지우면 빈 문자열을
  // 주지만 그 경로는 여기서 닫힌다).
  const handleCustomFromChange = useCallback((dateStr: string) => {
    if (!dateStr) return;
    setActivePreset('custom');
    onChange({ ...value, dateFrom: localStartOfDay(dateStr) });
  }, [onChange, value]);

  const handleCustomToChange = useCallback((dateStr: string) => {
    if (!dateStr) return;
    setActivePreset('custom');
    onChange({ ...value, dateTo: localEndOfDay(dateStr) });
  }, [onChange, value]);

  // ISO string -> YYYY-MM-DD for input[type=date]
  const fromDateStr = value.dateFrom ? toLocalDateString(new Date(value.dateFrom)) : '';
  const toDateStr = value.dateTo ? toLocalDateString(new Date(value.dateTo)) : '';

  // 선택/비선택 색은 목록 페이지들의 토글 칩과 같은 규약을 쓴다 — 같은 "고르는 버튼"이
  // 페이지마다 다른 색을 내지 않도록.
  const presetBtnClass = (preset: Preset) =>
    `rounded-control border px-3 py-1.5 text-sm font-semibold transition-colors ${
      activePreset === preset
        ? 'border-brand-600 bg-brand-600 text-white'
        : 'border-line bg-white text-ink-600 hover:bg-surface-hover hover:text-ink-900'
    }`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button onClick={() => handlePreset('today')} className={presetBtnClass('today')}>
        오늘
      </button>
      <button onClick={() => handlePreset('week')} className={presetBtnClass('week')}>
        1주일
      </button>
      <button onClick={() => handlePreset('month')} className={presetBtnClass('month')}>
        한달
      </button>
      <span className="mx-1 text-ink-300">|</span>
      {/*
        두 칸은 공용 `DateInput`이다(2026-10-27 이관). 손조립 「버튼 + sr-only + showPicker」
        사본을 걷었고 그 자리에서 셋이 함께 고쳐졌다: showPicker를 try/catch 없이 불러
        NotAllowedError가 미처리 예외로 새던 것 · 숨은 입력이 `aria-hidden`이라 접근명이
        **0건**이던 것 · 날짜 표기 손사본.

        ⛔ 활성색을 `className`으로 넘기지 말 것 — 공용의 중립색이 생성 CSS 순서상 뒤에 와서
        이기므로 색은 붙고 **표시만 조용히 사라진다**(red 없음). 그래서 활성은 `active` prop이고
        색은 공용이 소유한다(근거·측정값은 `packages/ui`의 `STATE_CLASS` 머리말).

        ⚠️ 모양은 공용 `sm` 밀도를 **그대로 받는다** — 종전 `px-3 gap-1.5`에서 `px-2 gap-2`로
        바뀌어 가로 패딩이 한 단계 좁아진다. 덮지 않는 이유는 그 둘이 공용 리터럴에 **있는**
        속성이라 승패가 또 생성 순서에 달리기 때문이다(실측: `px-3`은 이기고 `gap-1.5`는 진다).
        그런 축은 호출부가 손대지 않는 것이 이 공용화의 규약이고, `font-medium`처럼 리터럴에
        **없는** 속성만 넘긴다.
      */}
      <div className="flex items-center gap-1.5">
        <DateInput
          label="시작일"
          placeholder="시작일"
          value={fromDateStr}
          onChange={handleCustomFromChange}
          formatValue={formatDateCompact}
          active={activePreset === 'custom' && !!fromDateStr}
          size="sm"
          className="select-none font-medium"
        />
        <span className="text-ink-400 text-sm">~</span>
        <DateInput
          label="종료일"
          placeholder="종료일"
          value={toDateStr}
          onChange={handleCustomToChange}
          formatValue={formatDateCompact}
          active={activePreset === 'custom' && !!toDateStr}
          size="sm"
          className="select-none font-medium"
        />
      </div>
    </div>
  );
}

export { getPresetRange };
export default DateRangeFilter;
